(function () {
  const KEY = "heart-wishlist";
  const SB_URL = window.SB_URL;
  const SB_KEY = window.SB_KEY;

  if (!window.supabase || !SB_URL || !SB_KEY) return;

  const sb = window.supabase.createClient(SB_URL, SB_KEY);

  /* ── Cookie helpers (survive localStorage.clear) ── */
  const COOKIE_NAME = "sb_refresh_token";

  const setCookie = (value, days) => {
    const d = new Date();
    d.setTime(d.getTime() + days * 86400000);
    document.cookie =
      COOKIE_NAME +
      "=" +
      encodeURIComponent(value) +
      ";expires=" +
      d.toUTCString() +
      ";path=/;SameSite=Lax;Secure";
  };

  const getCookie = () => {
    const match = document.cookie.match(
      new RegExp("(?:^|; )" + COOKIE_NAME + "=([^;]*)"),
    );
    return match ? decodeURIComponent(match[1]) : null;
  };

  const deleteCookie = () => {
    document.cookie =
      COOKIE_NAME + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
  };

  /* ── localStorage helpers (guest-only scratchpad) ── */
  const readLocal = () => {
    try {
      const data = JSON.parse(localStorage.getItem(KEY) || "[]");
      if (!Array.isArray(data)) return [];
      return data
        .map((entry) => {
          if (typeof entry === "string")
            return { handle: entry, timestamp: Date.now() };
          return {
            handle: entry?.handle,
            timestamp: Number(entry?.timestamp) || Date.now(),
          };
        })
        .filter((x) => x.handle);
    } catch {
      return [];
    }
  };

  const clearLocal = () => {
    localStorage.removeItem(KEY);
  };

  /* ── Supabase helpers ── */
  const getUser = async () => {
    const {
      data: { user },
    } = await sb.auth.getUser();
    return user;
  };

  const recoverSession = async () => {
    const refreshToken = getCookie();
    if (!refreshToken) return null;
    try {
      const { data } = await sb.auth.refreshSession({
        refresh_token: refreshToken,
      });
      if (data?.user) {
        const newRefresh = data?.session?.refresh_token;
        if (newRefresh) setCookie(newRefresh, 365);
        return data.user;
      }
      deleteCookie();
      return null;
    } catch {
      deleteCookie();
      return null;
    }
  };

  const resolveUser = async () => {
    let user = await getUser();
    if (!user) user = await recoverSession();
    return user;
  };

  const fetchRemote = async (userId) => {
    const { data, error } = await sb
      .from("wishlist_items")
      .select("product_handle, liked_at")
      .eq("user_id", userId)
      .order("liked_at", { ascending: false });

    if (error) throw error;

    return (data || []).map((row) => ({
      handle: row.product_handle,
      timestamp: new Date(row.liked_at).getTime(),
    }));
  };

  const upsertAll = async (userId, items) => {
    if (!items.length) return;
    const rows = items.map((x) => ({
      user_id: userId,
      product_handle: x.handle,
      liked_at: new Date(x.timestamp).toISOString(),
    }));
    const { error } = await sb
      .from("wishlist_items")
      .upsert(rows, { onConflict: "user_id,product_handle" });
    if (error) throw error;
  };

  const mergeByNewest = (dbItems, localItems) => {
    const map = new Map();
    [...dbItems, ...localItems].forEach((item) => {
      if (!item?.handle) return;
      const current = map.get(item.handle);
      if (!current || Number(item.timestamp) > Number(current.timestamp)) {
        map.set(item.handle, item);
      }
    });
    return Array.from(map.values()).sort(
      (a, b) => Number(b.timestamp) - Number(a.timestamp),
    );
  };

  /* ── Button state ── */
  const setButtonState = (state) => {
    const btn = document.getElementById("save-wishlist");
    if (!btn) return;

    if (state === "connected") {
      const hasUnsaved = readLocal().length > 0;
      btn.textContent = hasUnsaved
        ? "Save new items to account"
        : "Wishlist saved to account";
      btn.disabled = !hasUnsaved;
      btn.style.opacity = hasUnsaved ? "1" : "0.7";
      btn.style.cursor = hasUnsaved ? "pointer" : "default";
      return;
    }
    if (state === "pending") {
      btn.textContent = "Check your email for login link";
      btn.disabled = true;
      btn.style.opacity = "0.8";
      return;
    }
    if (state === "error") {
      btn.textContent = "Could not send login link";
      btn.disabled = false;
      btn.style.opacity = "1";
      return;
    }
    // default
    const hasLocal = readLocal().length > 0;
    btn.textContent = hasLocal
      ? "Save wishlist to account"
      : "Connect account to load wishlist";
    btn.disabled = false;
    btn.style.opacity = "1";
    btn.style.cursor = "pointer";
  };

  /* ──────────────────────────────────────────────────
     getHandles()  ← THE CORE FUNCTION
     Called by the wishlist page renderer.
     Returns Promise<string[]> of product handles.

     Authenticated → DB is the source of truth.
                     Unsaved local items shown alongside
                     but NOT written to DB until explicit save.
     Guest         → localStorage only.
  ────────────────────────────────────────────────── */
  const getHandles = async () => {
    try {
      const user = await resolveUser();
      if (!user) {
        // Guest mode — localStorage only
        const local = readLocal();
        console.log(
          "[Wishlist] Guest mode — showing",
          local.length,
          "local items",
        );
        return local.map((x) => x.handle);
      }

      // Authenticated — DB is source of truth
      const dbItems = await fetchRemote(user.id);
      const localItems = readLocal();

      console.log(
        "[Wishlist] DB items:",
        dbItems.length,
        "| Unsaved local items:",
        localItems.length,
      );

      // Merge DB + any unsaved local items for display
      // (local items are NOT persisted to DB until explicit save)
      const merged = mergeByNewest(dbItems, localItems);

      console.log("[Wishlist] Rendering", merged.length, "items (DB-first)");
      return merged.map((x) => x.handle);
    } catch (err) {
      console.error("[Wishlist] getHandles error:", err);
      // Fallback to localStorage on any error
      return readLocal().map((x) => x.handle);
    }
  };

  /* ── init() — auth recovery + button state only ── */
  const init = async () => {
    try {
      const user = await resolveUser();
      setButtonState(user ? "connected" : "default");
      // Trigger a re-render on the wishlist page
      window.dispatchEvent(new CustomEvent("wishlist:updated"));
      if (typeof window.syncHearts === "function") window.syncHearts();
    } catch (err) {
      console.error("Supabase wishlist init error:", err);
    }
  };

  /* ── saveToAccount() — push local → DB, clear local ── */
  const saveToAccount = async () => {
    let user = await resolveUser();

    if (user) {
      try {
        const localItems = readLocal();
        if (localItems.length) {
          await upsertAll(user.id, localItems);
          console.log(
            "[Wishlist] Saved",
            localItems.length,
            "local items to DB",
          );
        }
        // Clear localStorage — DB is now the single source of truth
        clearLocal();
        setButtonState("connected");
        // Re-render — will now read from DB directly
        window.dispatchEvent(new CustomEvent("wishlist:updated"));
        if (typeof window.syncHearts === "function") window.syncHearts();
      } catch (err) {
        console.error("Supabase wishlist save error:", err);
        setButtonState("error");
      }
      return;
    }

    // Not authenticated — send OTP
    const email = window.CUSTOMER_EMAIL;
    if (!email) {
      console.warn(
        "Shopify customer email missing. User must be logged into Shopify.",
      );
      return;
    }
    const { error } = await sb.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.href },
    });
    if (error) {
      console.error(error);
      setButtonState("error");
      return;
    }
    setButtonState("pending");
  };

  /* ── onToggle() — heart click, write to DB if authed ── */
  const onToggle = async (handle, isLiked, timestamp) => {
    try {
      const user = await getUser();
      if (!user) return; // guest — localStorage handled by custom-js.js

      if (isLiked) {
        const { error } = await sb.from("wishlist_items").upsert(
          {
            user_id: user.id,
            product_handle: handle,
            liked_at: new Date(timestamp).toISOString(),
          },
          { onConflict: "user_id,product_handle" },
        );
        if (error) throw error;
      } else {
        const { error } = await sb
          .from("wishlist_items")
          .delete()
          .eq("user_id", user.id)
          .eq("product_handle", handle);
        if (error) throw error;
      }
    } catch (err) {
      console.error("Supabase wishlist toggle error:", err);
    }
  };

  /* ── Public API ── */
  window.sbWishlist = { init, onToggle, saveToAccount, getHandles };

  document.addEventListener("DOMContentLoaded", () => {
    init();
    const btn = document.getElementById("save-wishlist");
    if (btn) btn.addEventListener("click", saveToAccount);

    sb.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        if (session.refresh_token) setCookie(session.refresh_token, 365);
        init();
      } else {
        deleteCookie();
        setButtonState("default");
      }
    });
  });
})();
